<i18n>
{
	"en": {
		"theAddress": "the address",
		"cancelLabel": "Cancel",
		"saveAddress": "Edit Address",
		"title": "Edit Address",
		"name": "Name",
		"street": "Street",
		"postalPlace": "City",
		"postalCode": "Postal Code",
		"countryCode": "Country Code",
		"failedToFetch": "Failed to search for city by postal code"
	},
	"no": {
		"theAddress": "adressen",
		"cancelLabel": "Avbryt",
		"saveAddress": "Lagre adresse",
		"title": "Rediger adresse",
		"name": "Navn",
		"street": "Gate",
		"postalPlace": "Poststed",
		"postalCode": "Postnummer",
		"countryCode": "Landkode",
		"failedToFetch": "Kunne ikke søke etter poststed med postnummer"
	}
}
</i18n>

<template>
	<t-dialog
		:icon="icon"
		:isShown="isShown"
		:title="$t('title')"
		:confirmLabel="$t('saveAddress')"
		:cancelLabel="$t('cancelLabel')"
		:isConfirmDisabled="!canSave"
		disableCloseOnConfirm
		iconType="bp"
		@closing="checkClose"
		@ok="onOk"
		@close="onClose"
	>
		<t-form-group v-if="editableAddress" rowGap="5px">
			<t-text-input
				v-model="editableAddress.name"
				:label="$t('name')"
				:isInvalid="errors.invalidName"
				@input="setDirty"
			/>
			<t-text-input
				v-model="editableAddress.street"
				:label="$t('street')"
				@input="setDirty"
			/>
			<t-text-input
				v-model="editableAddress.countryCode"
				:label="$t('countryCode')"
				@input="fetchGlobalPostalPlace"
				@change="forceGlobalPostalPlace"
			/>
			<t-text-input
				v-model="editableAddress.postalCode"
				:label="$t('postalCode')"
				:isInvalid="errors.invalidPostalCode"
				@input="fetchGlobalPostalPlace"
				@change="forceGlobalPostalPlace"
			/>
			<t-text-input v-model="editableAddress.postalPlace" :label="$t('postalPlace')" @input="cancelFetch"/>
		</t-form-group>

		<ConfirmUnsavedChanges
			:isShown="confirmUnsavedChanges"
			:subject="$t('theAddress')"
			@cancel="toggleUnsavedChanges(false)"
			@save="onOk"
			@close="onCloseDirty"
		/>
	</t-dialog>
</template>

<script>
import TTextInput from '@/components/evergreen/inputs/TTextInput';
import TFormGroup from '@/components/evergreen/timpex/inputs/TFormGroup';
import { mapState, mapActions } from '../store/tripAddresses.store.js';
import deepCopy from 'deepcopy';
import ConfirmUnsavedChanges from '@/components/modals/ConfirmUnsavedChanges';

export default {
	components: { TTextInput, TFormGroup, ConfirmUnsavedChanges },
	props: {
		address: Object,
		icon: String,
		isShown: Boolean,
	},
	data() {
		return {
			editableAddress: undefined,
			fetchTimeout: undefined,
			confirmUnsavedChanges: false,
			dirty: false,
			closeDirty: false
		};
	},
	computed: {
		...mapState(['errors']),
		canSave() {
			return this.editableAddress && this.editableAddress.name && this.editableAddress.postalCode;
		}
	},
	watch: {
		address: {
			immediate: true,
			handler(newValue, oldValue) {
				if (newValue !== oldValue && newValue)
					this.setupEditableAddress(newValue);
			}
		}
	},
	methods: {
		...mapActions(['getGlobalPostalPlace',]),
		onClose() {
			this.toggleUnsavedChanges(false);
			this.$emit('close');
			this.dirty = false;
		},
		async onOk() {
			this.$emit('ok', this.editableAddress);
			this.dirty = false;
		},
		setupEditableAddress(address) {
			this.editableAddress = deepCopy(address);
			this.dirty = false;
		},
		processGlobalPostalPlace() {
			this.getGlobalPostalPlace(this.editableAddress)
				.then(globalPostalPlace => {
					if (globalPostalPlace.postalPlace) {
						this.editableAddress.postalPlace = globalPostalPlace.postalPlace;
					}
					if (globalPostalPlace.countryCode) {
						this.editableAddress.countryCode = globalPostalPlace.countryCode;
					}
					this.setDirty();
					this.$forceUpdate();
				})
				.catch ( () => {
					this.$notify({
						type: 'error',
						title: this.$t('failedToFetch')
					});
				});
		},
		fetchGlobalPostalPlace() {
			clearTimeout(this.fetchTimeout);
			this.fetchTimeout = setTimeout(() => {
				this.processGlobalPostalPlace();
			},200);
		},
		cancelFetch() {
			this.dirty = true;
			clearTimeout(this.fetchTimeout);
		},
		forceGlobalPostalPlace() {
			clearTimeout(this.fetchTimeout);
			this.processGlobalPostalPlace();
		},
		setDirty() {
			this.dirty = true;
		},
		onCloseDirty() {
			this.closeDirty = true;
			this.setupEditableAddress(this.address);
			this.onClose();
		},
		toggleUnsavedChanges(value) {
			this.confirmUnsavedChanges = value;
		},
		checkClose(abort) {
			if (this.dirty && !this.closeDirty) {
				abort(true);
				this.toggleUnsavedChanges(true);
			} else {
				abort(false);
			}
			this.closeDirty = false;
		},
	}
};
</script>
